import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MoneyDrawer } from "./CryptoTab";

/* ===== Crypto Analytics — the Indian Analytics tab's interaction language
   applied to the crypto validation books. Facet chips (book / symbol / side /
   exit / quote) are all active by default — deselect to exclude. Every
   aggregate row opens a right-side drawer with its trades; clicking a trade
   opens the full MoneyDrawer. All money INR; 1R = riskInr (₹10k default). */

const rup = (v) => `${v >= 0 ? "+" : "-"}₹${Math.abs(Math.round(v)).toLocaleString("en-IN")}`;
const when = (v) => v ? new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-";
const dayOf = (t) => String(t.exit_time || t.entry_time).slice(0, 10);

export const bookOf = (t) =>
  t.book === "OBS" ? "15m OB-S gold"
  : t.book === "OBX" ? "1h OB-X gold"
  : t.book === "OB" ? "1h OB-retest"
  : t.side === "SHORT" ? "4h short"
  : String(t.symbol).startsWith("PAXG") || String(t.symbol).startsWith("XAU") ? "4h gold"
  : t.tf === "1d" ? "1d trail" : `${t.tf} ST-trail`;
const sideOf = (t) => t.side || "LONG";
const exitOf = (t) => t.exit_reason || "?";
const quoteOf = (t) => t.quote === "USDT" ? "USDT" : "INR (legacy)";
const sugOf = (t) => t.suggested === "green" ? "suggested" : t.suggested === "watch" ? "watch" : "not suggested";
const MAJORS = ["BTCUSDT", "ETHUSDT", "SOLUSDT", "BNBUSDT", "XRPUSDT"];
const isMetal = (sym) => ["XAU", "XAG", "PAXG"].some(p => String(sym).startsWith(p));
const resultOf = (t) => { const r = t.r_mult ?? 0; return r > 0.1 ? "win" : r < -0.1 ? "loss" : "scratch"; };
const regimeOf = (t) => (t.aiRegime && t.aiRegime.regime) || "unstamped";
const IST_OPTS = { timeZone: "Asia/Kolkata" };
const dowOf = (t) => { try { return new Date(t.entry_time).toLocaleDateString("en-US", { weekday: "short", ...IST_OPTS }); } catch { return "?"; } };
const sessOf = (t) => { try { const h = +new Date(t.entry_time).toLocaleString("en-US", { hour: "2-digit", hour12: false, ...IST_OPTS }); return h >= 9 && h < 17 ? "IST day (9-17)" : h >= 17 ? "evening (17-24)" : "night (0-9)"; } catch { return "?"; } };
const holdOf = (t) => { const h = (Date.parse(t.exit_time) - Date.parse(t.entry_time)) / 3600000; if (isNaN(h)) return "?"; return h < 4 ? "<4h" : h < 24 ? "4-24h" : h < 72 ? "1-3d" : ">3d"; };
const bankStateOf = (t) => t.bank2Done ? "bank1+2" : t.tierDone ? "bank1/tier" : "clean";
const FACET_FNS = { book: bookOf, symbol: (t) => t.symbol, side: sideOf, exit: exitOf, quote: quoteOf, suggested: sugOf,
                    result: resultOf, regime: regimeOf, dow: dowOf, session: sessOf, hold: holdOf, bank: bankStateOf };
const FACET_TITLES = { book: "Book", symbol: "Symbol", side: "Side", exit: "Exit", quote: "Quote", suggested: "Suggested",
                       result: "Result", regime: "AI Regime", dow: "Day", session: "Session", hold: "Hold", bank: "Banked" };
const R_BUCKETS = ["≤ -1R", "-1..0R", "0..+1R", "+1..+3R", "+3..+5R", "> +5R"];
const rBucket = (t) => {
  const r = t.r_mult ?? (t.pnl_inr != null && t.riskInr ? t.pnl_inr / t.riskInr : null);
  if (r == null) return "0..+1R";
  if (r <= -1) return "≤ -1R";
  if (r <= 0) return "-1..0R";
  if (r <= 1) return "0..+1R";
  if (r <= 3) return "+1..+3R";
  if (r <= 5) return "+3..+5R";
  return "> +5R";
};
const chargesOf = (t) => (t.entryFee || 0) + (t.tierFee || 0) + (t.exitFee || 0) + (t.fundingFee || 0);

function groupStats(trades, keyFn, sortDayDesc = false) {
  const m = new Map();
  for (const t of trades) {
    const k = keyFn(t);
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(t);
  }
  const rows = [...m.entries()].map(([label, ts]) => ({
    label, trades: ts, n: ts.length,
    wins: ts.filter(t => (t.pnl_inr || 0) > 0).length,
    pnl: ts.reduce((a, t) => a + (t.pnl_inr || 0), 0),
    r: ts.reduce((a, t) => a + (t.r_mult || 0), 0),
  }));
  return sortDayDesc ? rows.sort((a, b) => b.label.localeCompare(a.label))
                     : rows.sort((a, b) => b.pnl - a.pnl);
}

function Chip({ label, active, onClick, onOnly, stat }) {
  const net = stat ? stat.net : null;
  const bg = !active ? "var(--panel3)"
    : net == null ? "#0e7490"
    : net >= 0 ? `rgba(34,160,80,${Math.min(0.9, 0.3 + Math.abs(net) / 60000)})`
    : `rgba(170,50,45,${Math.min(0.9, 0.3 + Math.abs(net) / 60000)})`;
  return (
    <button onClick={onClick} onDoubleClick={(e) => { e.preventDefault(); onOnly && onOnly(); }} style={{
      background: bg, color: active ? "#fff" : "var(--muted2)",
      border: `1px solid ${active ? "#06b6d4" : "var(--border2)"}`, borderRadius: 14,
      padding: "3px 10px", fontSize: 11, cursor: "pointer", opacity: active ? 1 : 0.55,
    }} title={`${stat ? `${stat.n} trades · net ${(net >= 0 ? "+" : "−")}₹${Math.abs(Math.round(net || 0)).toLocaleString("en-IN")}` : ""}
click: toggle · double-click: ONLY this`}>
      {label}{stat ? ` (${stat.n})` : ""}
    </button>
  );
}

function ChipGroup({ title, values, selected, onToggle, onSetAll, onOnly, stats, sortByNet, searchable }) {
  const [q, setQ] = useState("");
  const allOn = values.every(v => selected.has(v));
  const linkStyle = { background: "none", border: "none", color: "#06b6d4", fontSize: 10, cursor: "pointer", padding: "0 2px", textDecoration: "underline" };
  let vals = values;
  if (sortByNet && stats) vals = [...values].sort((a, b) => ((stats[b] || {}).net || 0) - ((stats[a] || {}).net || 0));
  if (searchable && q) vals = vals.filter(v => String(v).toLowerCase().includes(q.toLowerCase()));
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted2)", width: 60, flexShrink: 0 }}>{title}</span>
      <span style={{ display: "flex", gap: 2, flexShrink: 0 }}>
        <button style={{ ...linkStyle, color: allOn ? "var(--faint2)" : "#06b6d4" }} disabled={allOn} onClick={() => onSetAll(true)}>all</button>
        <span style={{ color: "var(--border2)", fontSize: 10 }}>/</span>
        <button style={{ ...linkStyle, color: selected.size === 0 ? "var(--faint2)" : "#06b6d4" }} disabled={selected.size === 0} onClick={() => onSetAll(false)}>none</button>
      </span>
      {searchable && values.length > 12 && (
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="search…"
          style={{ background: "var(--panel3)", color: "var(--text2)", border: "1px solid var(--border2)", borderRadius: 10, padding: "2px 8px", fontSize: 11, width: 90 }} />
      )}
      {vals.map(v => <Chip key={v} label={v} active={selected.has(v)} onClick={() => onToggle(v)}
        onOnly={onOnly ? () => onOnly(v) : null} stat={stats ? (stats[v] || { n: 0, net: 0 }) : null} />)}
    </div>
  );
}

function Panel({ id, title, subtitle, children }) {
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem("cra-panel-" + id) === "1"; } catch { return false; } });
  const toggle = () => setCollapsed(c => { const n = !c; try { localStorage.setItem("cra-panel-" + id, n ? "1" : "0"); } catch {} return n; });
  return (
    <div style={{ background: "var(--chart-bg)", border: "1px solid var(--panel3)", borderRadius: 8, padding: "10px 12px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: collapsed ? 0 : 6 }}>
        <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted2)", fontWeight: 700 }}>
          {title} {subtitle && <span style={{ fontWeight: 400, textTransform: "none" }}>{subtitle}</span>}
        </div>
        <button onClick={toggle} style={{ background: "none", border: "1px solid var(--border2)", borderRadius: 4, color: "var(--muted2)", fontSize: 11, padding: "0 7px", cursor: "pointer", lineHeight: "16px" }}>
          {collapsed ? "▸" : "▾"}
        </button>
      </div>
      {!collapsed && children}
    </div>
  );
}

/* 10-row paging (user, 01 Oct 2026) — same component as Indian Analytics */
function Paged({ items, per = 10, render }) {
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [items.length]);
  const pages = Math.max(1, Math.ceil(items.length / per));
  const p = Math.min(page, pages - 1);
  return (
    <>
      {render(items.slice(p * per, (p + 1) * per))}
      {pages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, fontSize: 11, color: "var(--muted2)", margin: "6px 2px" }}>
          <button className="btn btn-sm btn-secondary" disabled={p === 0} onClick={() => setPage(p - 1)}>‹ Prev</button>
          <span>page {p + 1} / {pages} · {items.length} rows</span>
          <button className="btn btn-sm btn-secondary" disabled={p >= pages - 1} onClick={() => setPage(p + 1)}>Next ›</button>
        </div>
      )}
    </>
  );
}

/* aggregate table: rows of {label, n, wins, pnl, r}; click row -> slice drawer */
function AggTable({ rows, onPick, extraCol }) {
  return (
    <Paged items={rows} render={pageRows => (
    <div className="tl-table-wrap">
      <table className="stocks-table" style={{ width: "100%" }}>
        <thead><tr><th></th><th>Trades</th><th>WR</th><th>Net P&L</th><th>Σ R</th>{extraCol && <th>{extraCol[0]}</th>}</tr></thead>
        <tbody>
          {pageRows.map(r => (
            <tr key={r.label} onClick={() => onPick(r)} style={{ cursor: "pointer" }} title="Click to list these trades">
              <td style={{ fontWeight: 600 }}>{r.label}</td>
              <td>{r.n}</td>
              <td>{Math.round(100 * r.wins / r.n)}%</td>
              <td className={r.pnl >= 0 ? "positive" : "negative"}>{rup(r.pnl)}</td>
              <td className={r.r >= 0 ? "positive" : "negative"}>{r.r >= 0 ? "+" : ""}{r.r.toFixed(2)}R</td>
              {extraCol && <td>{extraCol[1](r)}</td>}
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={extraCol ? 6 : 5} style={{ color: "var(--muted2)" }}>no trades in this slice</td></tr>}
        </tbody>
      </table>
    </div>
    )} />
  );
}

/* right-side drawer listing one slice's trades */
function SliceDrawer({ group, onClose, onPickTrade, planLev, planFrac, specs, tradeOpen }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !tradeOpen) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, tradeOpen]);
  const ts = group.trades;
  const wins = ts.filter(t => (t.pnl_inr || 0) > 0).length;
  const pnl = ts.reduce((a, t) => a + (t.pnl_inr || 0), 0);
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1190 }} />
      <div style={{ position: "fixed", top: 0, right: 0, height: "100vh", zIndex: 1191, width: "min(860px, 96vw)", background: "var(--panel2)", borderLeft: "1px solid var(--border2)", boxShadow: "-8px 0 30px rgba(0,0,0,0.5)", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid var(--panel3)" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text2)" }}>{group.label}</div>
            <div style={{ fontSize: 11, color: "var(--muted2)", marginTop: 2 }}>
              {ts.length} trades · {wins}W {ts.length - wins}L ({ts.length ? Math.round(100 * wins / ts.length) : 0}%) ·{" "}
              <span style={{ color: pnl >= 0 ? "#34d399" : "#f87171", fontWeight: 700 }}>{rup(pnl)}</span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "var(--panel3)", color: "var(--text2)", border: "1px solid var(--border2)", borderRadius: 4, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ overflowY: "auto", flex: 1, padding: "8px 16px" }}>
          <Paged items={ts} render={pageTs => (
          <div className="tl-table-wrap">
            <table className="stocks-table" style={{ width: "100%" }}>
              <thead><tr>
                <th>Entry</th><th>Exit</th><th>Symbol</th><th>Book</th><th>Reason</th>
                <th title="Notional position value at entry (qty × price in ₹)">Capital</th>
                <th title="What a live account pays to hold it: capital ÷ planned leverage (= the config fraction × this contract's Shark max)">Margin</th>
                <th title="Entry-leg trading fee (maker/taker as filled)">Fee buy</th>
                <th title="Exit-leg fee — includes the tier-sell fee when 50% was banked">Fee sell</th>
                <th title="Funding paid (longs) or received (shorts) across 8h stamps held">Funding</th>
                <th>Net P&L</th><th>R</th>
              </tr></thead>
              <tbody>
                {pageTs.map(t => {
                  const cap = t.notional || t.entry * t.qty * (t.fxEntry || 1);
                  const maxLev = specs?.[t.symbol]?.maxLeverage;
                  const ideal = cap / (1.5 * (t.riskInr || 10000));
                  const lev = Math.max(1, Math.floor(Math.min(maxLev || ideal, ideal)));
                  return (
                    <tr key={t.id} onClick={() => onPickTrade(t)} style={{ cursor: "pointer" }} title="Full money breakdown">
                      <td style={{ whiteSpace: "nowrap" }}>{when(t.entry_time)}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{when(t.exit_time)}</td>
                      <td style={{ fontWeight: 600 }}>{t.symbol}{t.ref && <div style={{ fontSize: 9, fontWeight: 400, color: "var(--muted2)" }}>{t.ref}</div>}</td>
                      <td style={{ fontSize: 11 }}>{bookOf(t)}</td>
                      <td style={{ fontSize: 11 }}>{t.exit_reason}{t.tierDone ? " 🏦" : ""}</td>
                      <td>₹{Math.round(cap).toLocaleString("en-IN")}</td>
                      <td>₹{Math.round(cap / lev).toLocaleString("en-IN")}<span style={{ fontSize: 9, color: "var(--muted2)" }}> @{lev}x</span></td>
                      <td style={{ color: "var(--muted2)" }}>₹{Math.round(t.entryFee || 0).toLocaleString("en-IN")}</td>
                      <td style={{ color: "var(--muted2)" }}>₹{Math.round((t.exitFee || 0) + (t.tierFee || 0)).toLocaleString("en-IN")}</td>
                      <td style={{ color: (t.fundingFee || 0) < 0 ? "var(--pos)" : "var(--muted2)" }}>{(t.fundingFee || 0) < 0 ? "−" : ""}₹{Math.abs(Math.round(t.fundingFee || 0)).toLocaleString("en-IN")}</td>
                      <td className={(t.pnl_inr || 0) >= 0 ? "positive" : "negative"} style={{ fontWeight: 700 }}>{rup(t.pnl_inr || 0)}</td>
                      <td className={(t.r_mult || 0) >= 0 ? "positive" : "negative"}>{t.r_mult != null ? `${t.r_mult >= 0 ? "+" : ""}${t.r_mult}R` : "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )} />
        </div>
      </div>
    </>
  );
}

export default function CryptoAnalytics() {
  const [st, setSt] = useState(null);
  const [err, setErr] = useState("");
  const [period, setPeriod] = useState("ALL"); // ALL | 7D | 30D | CUSTOM
  const [fScope, setFScope] = useState("NOW"); // NOW = current trading plan (default) | ALL = full history
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [slice, setSlice] = useState(null);   // {label, trades}
  const [detail, setDetail] = useState(null); // MoneyDrawer trade

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get("/api/crypto/trader/status", { timeout: 15000 });
        if (r.data.success) { setSt(r.data); setErr(""); }
      } catch (e) { setErr(e.message); }
    };
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const allClosed = useMemo(() => (st?.closedTrades || []).filter(t => t.pnl_inr != null), [st]);
  // "Now trading" scope (same rule as the Crypto tab): symbol in the live
  // universe AND book enabled — legacy alts / paused books live under ALL.
  const closed = useMemo(() => {
    if (fScope !== "NOW") return allClosed;
    const universe = st?.symbols || [];
    return allClosed.filter(t =>
      t.book === "OBX" ? !!(st?.config?.obxEnabled ?? 1)
        : t.book === "OBS" ? !!(st?.config?.obsEnabled ?? 1)
          : t.book === "OB" ? !!(st?.config?.obEnabled) && universe.includes(t.symbol)
            : universe.includes(t.symbol));
  }, [allClosed, fScope, st]);

  const dateFiltered = useMemo(() => {
    if (period === "ALL") return closed;
    if (period === "CUSTOM") return closed.filter(t => {
      const d = dayOf(t);
      return (!fromDate || d >= fromDate) && (!toDate || d <= toDate);
    });
    const days = parseInt(period, 10);
    const cutoff = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
    return closed.filter(t => dayOf(t) >= cutoff);
  }, [closed, period, fromDate, toDate]);

  const facets = useMemo(() => {
    const uniq = (fn, order) => {
      const vals = [...new Set(closed.map(fn))];
      return order ? order.filter(v => vals.includes(v)).concat(vals.filter(v => !order.includes(v))) : vals.sort();
    };
    return {
      book: uniq(bookOf, ["1d trail", "4h ST-trail", "1h ST-trail", "1h OB-retest", "1h OB-X gold", "15m OB-S gold", "4h gold", "4h short"]),
      symbol: uniq(t => t.symbol),
      side: uniq(sideOf, ["LONG", "SHORT"]),
      exit: uniq(exitOf),
      quote: uniq(quoteOf, ["USDT", "INR (legacy)"]),
      suggested: uniq(sugOf, ["suggested", "watch", "not suggested"]),
      result: uniq(resultOf, ["win", "scratch", "loss"]),
      regime: uniq(regimeOf, ["RISK_ON", "NEUTRAL", "RISK_OFF", "unstamped"]),
      dow: uniq(dowOf, ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]),
      session: uniq(sessOf, ["IST day (9-17)", "evening (17-24)", "night (0-9)"]),
      hold: uniq(holdOf, ["<4h", "4-24h", "1-3d", ">3d"]),
      bank: uniq(bankStateOf, ["clean", "bank1/tier", "bank1+2"]),
    };
  }, [closed]);

  useEffect(() => {
    (async () => {
      try { const r = await axios.get("/api/crypto/trader/pnl-paths", { timeout: 60000 }); setPaths(r.data?.paths || null); } catch {}
    })();
  }, []);
  const [sel, setSel] = useState(null);
  const [viewName, setViewName] = useState("");
  const [paths, setPaths] = useState(null);
  const [openSort, setOpenSort] = useState({ key: "u", dir: -1 });
  const [views, setViews] = useState(() => { try { return JSON.parse(localStorage.getItem("cra-views") || "[]"); } catch { return []; } });
  useEffect(() => {
    if (closed.length) setSel(prev => prev ||
      Object.fromEntries(Object.keys(facets).map(k => [k, new Set(facets[k])])));
  }, [closed, facets]);
  const toggle = (f, v) => setSel(prev => {
    const next = { ...prev, [f]: new Set(prev[f]) };
    if (next[f].has(v)) next[f].delete(v); else next[f].add(v);
    return next;
  });
  const setAll = (f, on) => setSel(prev => ({ ...prev, [f]: new Set(on ? facets[f] : []) }));
  const only = (f, v) => setSel(prev => {
    const cur = prev[f];
    const isSolo = cur && cur.size === 1 && cur.has(v);
    return { ...prev, [f]: new Set(isSolo ? facets[f] : [v]) };  // double-click again -> back to all
  });
  const resetAll = () => setSel(Object.fromEntries(Object.keys(facets).map(k => [k, new Set(facets[k])])));
  // a preset is a clean VIEW: it resets every facet, then applies its own slice
  // (stacking presets confused more than it helped — combine via chips instead)
  const preset = (changes) => setSel(Object.fromEntries(Object.keys(facets).map(k =>
    [k, new Set(changes[k] ? changes[k] : facets[k])])));

  const filtered = useMemo(() => {
    if (!sel) return [];
    return dateFiltered.filter(t => Object.entries(FACET_FNS).every(([k, fn]) => !sel[k] || sel[k].has(fn(t))));
  }, [dateFiltered, sel]);

  const sum = useMemo(() => {
    const pnl = filtered.reduce((a, t) => a + (t.pnl_inr || 0), 0);
    const r = filtered.reduce((a, t) => a + (t.r_mult || 0), 0);
    const wins = filtered.filter(t => (t.pnl_inr || 0) > 0).length;
    const fees = filtered.reduce((a, t) => a + (t.entryFee || 0) + (t.tierFee || 0) + (t.exitFee || 0), 0);
    const funding = filtered.reduce((a, t) => a + (t.fundingFee || 0), 0);
    const banked = filtered.filter(t => t.tierDone).length;
    const holds = filtered.map(t => (Date.parse(t.exit_time) - Date.parse(t.entry_time)) / 3600000).filter(h => !isNaN(h));
    return { pnl, r, wins, fees, funding, banked, n: filtered.length,
             avgHold: holds.length ? holds.reduce((a, b) => a + b, 0) / holds.length : 0 };
  }, [filtered]);

  // all-time accumulated (UNFILTERED — the "era total" like the Indian tabs)
  const allTime = useMemo(() => {
    const booked = st?.totalPnlInr || 0;
    const fx = st?.fx?.rate || 95.5;
    const feeSide = st?.params?.feePerSide ?? 0.000472;
    let unreal = 0;
    for (const t of (st?.openTrades || [])) {
      const lp = t.lastPrice; if (lp == null) continue;
      const k = t.quote === "USDT" ? fx : 1;
      const rem = t.remainingQty ?? t.qty;
      const sgn = t.side === "SHORT" ? -1 : 1;
      unreal += (t.bookedPnl || 0) + (lp - t.entry) * rem * sgn * k - (t.entryFee || 0) - lp * rem * feeSide * k - (t.fundingFee || 0);
    }
    const first = (st?.closedTrades || [])[0];
    return { booked, unreal, overall: booked + unreal, since: first ? String(first.entry_time).slice(0, 10) : "" };
  }, [st]);

  const planFrac = Math.min(1, Math.max(0.05, Number(st?.config?.planLevFrac ?? 0.4)));
  // stop-derived leverage: lev = 1/(1.5 x stop distance), capped at contract max
  const marginOf = (t) => {
    const cap = t.notional || t.entry * t.qty * (t.fxEntry || 1);
    const mx = st?.contractSpecs?.[t.symbol]?.maxLeverage;
    const ideal = cap / (1.5 * (t.riskInr || 10000));
    const lev = Math.max(1, Math.floor(Math.min(mx || ideal, ideal)));
    return { margin: cap / lev, lev, cap };
  };
  // invested (margin at the leverage rule) vs what it returned — per book
  const invested = useMemo(() => {
    const groups = groupStats(filtered, bookOf);
    const rows = groups.map(g => {
      const inv = g.trades.reduce((a, t) => a + marginOf(t).margin, 0);
      return { ...g, inv, roi: inv ? 100 * g.pnl / inv : 0 };
    });
    const totInv = rows.reduce((a, r) => a + r.inv, 0);
    const totPnl = rows.reduce((a, r) => a + r.pnl, 0);
    return { rows, totInv, totPnl, totRoi: totInv ? 100 * totPnl / totInv : 0, totN: filtered.length };
  }, [filtered, st]);

  const byBook = useMemo(() => groupStats(filtered, bookOf), [filtered]);
  const bySymbol = useMemo(() => groupStats(filtered, t => t.symbol), [filtered]);
  const byExit = useMemo(() => groupStats(filtered, exitOf), [filtered]);
  const byDay = useMemo(() => {
    const rows = groupStats(filtered, dayOf, true);
    let cum = 0;
    [...rows].reverse().forEach(r => { cum += r.pnl; r.cum = cum; });
    return rows;
  }, [filtered]);
  const rDist = useMemo(() => {
    const rows = groupStats(filtered, rBucket);
    return R_BUCKETS.map(b => rows.find(r => r.label === b)).filter(Boolean);
  }, [filtered]);

  if (!st) return <div className="loading"><div className="spinner" /><p>{err || "Loading crypto analytics…"}</p></div>;

  const openSlice = (label, trades) => setSlice({ label, trades });
  const stats = (fn) => dateFiltered.reduce((m, t) => {
    const k = fn(t); const e = m[k] = m[k] || { n: 0, net: 0 };
    e.n += 1; e.net += t.pnl_inr || 0; return m;
  }, {});

  return (
    <div className="crypto-analytics">
      <div className="dashboard-header">
        <div>
          <h2>Crypto Analytics <span style={{ fontSize: 12, color: "var(--muted2)", fontWeight: 400 }}>validation forward test · every ₹ figure net of fees & funding · 1R = ₹{Math.round(st.config?.riskPerTrade || 0).toLocaleString("en-IN")}</span></h2>
        </div>
      </div>

      {/* all-time accumulated total — unfiltered, the crypto era's running score */}
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "baseline", background: "var(--tint-pos)", border: "1px solid var(--panel3)", borderRadius: 8, padding: "10px 14px", marginBottom: 10 }}>
        <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted2)", fontWeight: 700 }}>Accumulated since {allTime.since}</span>
        <span style={{ fontSize: 13 }}>Booked (realized): <b className={allTime.booked >= 0 ? "positive" : "negative"} style={{ fontSize: 16 }}>{rup(allTime.booked)}</b></span>
        <span style={{ fontSize: 13 }}>Open unrealized: <b className={allTime.unreal >= 0 ? "positive" : "negative"}>{rup(allTime.unreal)}</b></span>
        <span style={{ fontSize: 13 }}>Overall: <b className={allTime.overall >= 0 ? "positive" : "negative"} style={{ fontSize: 16 }}>{rup(allTime.overall)}</b></span>
        <span style={{ fontSize: 10.5, color: "var(--muted2)" }}>ignores the filters below — this is the whole book, always</span>
      </div>

      {/* summary strip — CLOSED trades only, by design */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", background: "var(--chart-bg)", border: "1px solid var(--panel3)", borderRadius: 8, padding: "8px 14px", marginBottom: 10, fontSize: 12.5 }}>
        <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5, padding: "2px 6px", borderRadius: 4, background: "var(--btn-muted)", color: "var(--grey-light)", border: "1px solid var(--btn-border)" }} title="Every number on this tab (except the Open-now panel) is from CLOSED trades — realized results only. Running trades are unrealized and live in their own panel below.">CLOSED ONLY</span>
        <span><b>{sum.n}</b> closed · {sum.wins}W {sum.n - sum.wins}L ({sum.n ? Math.round(100 * sum.wins / sum.n) : 0}%)</span>
        <span>Net: <b className={sum.pnl >= 0 ? "positive" : "negative"}>{rup(sum.pnl)}</b> (<b className={sum.r >= 0 ? "positive" : "negative"}>{sum.r >= 0 ? "+" : ""}{sum.r.toFixed(2)}R</b>)</span>
        <span>🏦 tier-banked: {sum.banked}</span>
        <span>fees ₹{Math.round(sum.fees).toLocaleString("en-IN")} · funding ₹{Math.round(sum.funding).toLocaleString("en-IN")}</span>
        <span>avg hold {sum.avgHold < 48 ? sum.avgHold.toFixed(1) + "h" : (sum.avgHold / 24).toFixed(1) + "d"}</span>
        <span style={{ marginLeft: "auto", color: "var(--muted2)", fontSize: 11 }}>🔵 {st.openCount} running — see "Open now" panel, excluded from every stat here</span>
      </div>

      {/* running trades — LIVE/unrealized, deliberately separated from all closed stats */}
      {(st.openTrades || []).length > 0 && (() => {
        const fx = st.fx?.rate || 95.5;
        const feeSide = st.params?.feePerSide ?? 0.000472;
        const rows = st.openTrades.map(t => {
          const k = t.quote === "USDT" ? fx : 1;
          const lp = t.lastPrice; const rem = t.remainingQty ?? t.qty;
          const sgn = t.side === "SHORT" ? -1 : 1;
          const u = lp == null ? null
            : (t.bookedPnl || 0) + (lp - t.entry) * rem * sgn * k - (t.entryFee || 0) - lp * rem * feeSide * k - (t.fundingFee || 0);
          return { t, u, r: u != null && t.riskInr ? u / t.riskInr : null };
        });
        const tot = rows.reduce((a, r) => a + (r.u || 0), 0);
        const sortVal = {
          opened: (x) => x.t.entry_time || "",
          symbol: (x) => x.t.symbol || "",
          book: (x) => bookOf(x.t),
          side: (x) => sideOf(x.t),
          entry: (x) => x.t.entry || 0,
          ltp: (x) => x.t.lastPrice ?? -Infinity,
          u: (x) => x.u ?? -Infinity,
          r: (x) => x.r ?? -Infinity,
          prot: (x) => x.t.tierDone ? 3 : ((x.t.side === "SHORT" ? x.t.stop < x.t.entry : x.t.stop > x.t.entry) ? 2 : x.t.stArmed ? 1 : 0),
        }[openSort.key] || ((x) => 0);
        rows.sort((p, q) => {
          const a2 = sortVal(p), b2 = sortVal(q);
          return (a2 < b2 ? -1 : a2 > b2 ? 1 : 0) * openSort.dir;
        });
        const STh = ({ k, children, right }) => (
          <th onClick={() => setOpenSort(s2 => ({ key: k, dir: s2.key === k ? -s2.dir : -1 }))}
            style={{ cursor: "pointer", textAlign: right ? "right" : undefined, textDecoration: openSort.key === k ? "underline" : "none" }}
            title="Click to sort; click again to reverse">
            {children}{openSort.key === k ? (openSort.dir === -1 ? " ↓" : " ↑") : ""}
          </th>
        );
        return (
          <div style={{ background: "var(--chart-bg)", border: "1px dashed #06b6d4", borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "#06b6d4", fontWeight: 700, marginBottom: 6 }}>
              🔵 Open now — running trades (LIVE, unrealized) <span style={{ fontWeight: 400, textTransform: "none", color: "var(--muted2)" }}>— nothing below counts these; they join the stats only when they close</span>
            </div>
            <Paged items={rows} per={5} render={pageOpen => (
            <div className="tl-table-wrap">
              <table className="stocks-table" style={{ width: "100%" }}>
                <thead><tr><STh k="opened">Opened</STh><STh k="symbol">Symbol</STh><STh k="book">Book</STh><STh k="side">Side</STh><STh k="entry">Entry</STh><STh k="ltp">LTP</STh><STh k="u">Unrealized (net)</STh><STh k="r">≈ R</STh><STh k="prot">Protection</STh></tr></thead>
                <tbody>
                  {pageOpen.map(({ t, u, r }) => (
                    <tr key={t.id} className="trade-open-row" onClick={() => setDetail(t)} style={{ cursor: "pointer" }}>
                      <td style={{ whiteSpace: "nowrap" }}>{when(t.entry_time)}</td>
                      <td style={{ fontWeight: 600 }}>{t.symbol}{t.ref && <div style={{ fontSize: 9, fontWeight: 400, color: "var(--muted2)" }}>{t.ref}</div>}</td>
                      <td style={{ fontSize: 11 }}>{bookOf(t)}</td>
                      <td>{sideOf(t)}</td>
                      <td>{t.entry}</td>
                      <td><span className="live-premium">{t.lastPrice ?? "…"}</span></td>
                      <td className={u == null ? "" : u >= 0 ? "positive" : "negative"} style={{ fontWeight: 700 }}>
                        {u == null ? "…" : <span className="running-pnl">{rup(u)} <span className="pulse-dot" style={{ display: "inline-block" }}></span></span>}
                      </td>
                      <td className={(r || 0) >= 0 ? "positive" : "negative"}>{r != null ? `${r >= 0 ? "+" : ""}${r.toFixed(2)}R` : "-"}</td>
                      <td style={{ fontSize: 10.5 }}>{t.tierDone ? "🏦 BE runner" : ((t.side === "SHORT" ? t.stop < t.entry : t.stop > t.entry) ? "🔒 profit locked" : t.stArmed ? "📈 trailing" : "⏳ 1R at risk")}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr style={{ fontWeight: 700 }}>
                  <td colSpan={6} style={{ textAlign: "right", color: "var(--muted2)" }}>total unrealized</td>
                  <td className={tot >= 0 ? "positive" : "negative"}>{rup(tot)}</td><td colSpan={2}></td>
                </tr></tfoot>
              </table>
            </div>
            )} />
          </div>
        );
      })()}

      {/* period + facet chips */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, background: "var(--chart-bg)", border: "1px solid var(--panel3)", borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted2)", width: 60 }}>Show</span>
          <Chip label="🎯 Now trading" active={fScope === "NOW"} onClick={() => setFScope("NOW")} />
          <Chip label="All history" active={fScope === "ALL"} onClick={() => setFScope("ALL")} />
          <span style={{ fontSize: 10.5, color: "var(--muted2)" }}>
            {fScope === "NOW" ? "current universe + enabled books only (legacy alts & paused books hidden)" : "every trade ever"}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted2)", width: 60 }}>Period</span>
          {["ALL", "7D", "30D", "CUSTOM"].map(p => <Chip key={p} label={p} active={period === p} onClick={() => setPeriod(p)} />)}
          {period === "CUSTOM" && (<>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="ema-select" style={{ fontSize: 11 }} />
            <span style={{ fontSize: 11, color: "var(--muted2)" }}>to</span>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="ema-select" style={{ fontSize: 11 }} />
          </>)}
        </div>
        {sel && (<>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted2)", width: 60 }}>Presets</span>
            <button className="btn btn-sm btn-secondary" onClick={() => preset({ symbol: facets.symbol.filter(v => MAJORS.includes(v)) })}>Majors</button>
            <button className="btn btn-sm btn-secondary" onClick={() => preset({ symbol: facets.symbol.filter(v => !MAJORS.includes(v) && !isMetal(v)) })}>Alts</button>
            <button className="btn btn-sm btn-secondary" onClick={() => preset({ symbol: facets.symbol.filter(v => isMetal(v)) })}>Metals</button>
            <button className="btn btn-sm btn-secondary" onClick={() => preset({ suggested: ["suggested"] })}>🟢 Suggested</button>
            <button className="btn btn-sm btn-secondary" onClick={() => preset({ result: ["win"] })}>Winners</button>
            <button className="btn btn-sm btn-secondary" onClick={() => preset({ result: ["loss"] })}>Losers</button>
            <button className="btn btn-sm btn-secondary" style={{ color: "#f59e0b" }} onClick={resetAll}>✕ reset filters</button>
          </div>
          {(() => {
            const partial = Object.keys(facets).filter(k => sel[k] && sel[k].size < facets[k].length);
            const net = filtered.reduce((a, t) => a + (t.pnl_inr || 0), 0);
            const r = filtered.reduce((a, t) => a + (t.r_mult || 0), 0);
            const w = filtered.filter(t => (t.pnl_inr || 0) > 0).length;
            return (
              <div style={{ fontSize: 11.5, background: "var(--panel3)", borderRadius: 6, padding: "5px 8px", display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ fontWeight: 700 }}>Selection P&L:{" "}
                  <span className={net >= 0 ? "positive" : "negative"} style={{ fontSize: 13 }}>{rup(net)}</span>
                  <span className={r >= 0 ? "positive" : "negative"}> ({r >= 0 ? "+" : ""}{r.toFixed(1)}R)</span>
                </span>
                <span style={{ color: "var(--muted2)" }}>{filtered.length} trades · WR {filtered.length ? Math.round(100 * w / filtered.length) : 0}% · avg {filtered.length ? rup(net / filtered.length) : "-"}</span>
                {(() => {
                  if (!paths || !filtered.length) return null;
                  // max CONCURRENT capital: sweep entry/exit windows, sum notional
                  const evs = [];
                  for (const t of filtered) {
                    const p = paths[String(t.ref || t.id)];
                    const cap = p?.capital ?? (t.notional || 0);
                    // margin at the stop-derived leverage rule: lev = floor(notional / (1.5 x risk)),
                    // capped at the contract max -> margin ~ 1.5 x riskInr (the liquidation-safe sizing)
                    const maxLev = st.contractSpecs?.[t.symbol]?.maxLeverage || 10;
                    const lev = Math.max(1, Math.min(maxLev, t.riskInr ? Math.floor(cap / (1.5 * t.riskInr)) : 1));
                    const mar = cap / lev;
                    const e = Date.parse(t.entry_time), x = t.exit_time ? Date.parse(t.exit_time) : Date.now();
                    if (!isNaN(e)) { evs.push([e, cap, mar]); evs.push([x, -cap, -mar]); }
                  }
                  evs.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
                  let cur = 0, maxCap = 0, curM = 0, maxMar = 0;
                  for (const [, dc, dm] of evs) { cur += dc; curM += dm; if (cur > maxCap) maxCap = cur; if (curM > maxMar) maxMar = curM; }
                  // worst combined running P&L: sum hourly paths
                  const curve = new Map();
                  let covered = 0;
                  for (const t of filtered) {
                    const p = paths[String(t.ref || t.id)];
                    if (!p || !p.vals) continue;
                    covered++;
                    p.vals.forEach((v, i) => {
                      if (v == null) return;
                      const h = p.t0 + i * p.step;
                      curve.set(h, (curve.get(h) || 0) + v);
                    });
                  }
                  let worst = 0, worstH = null;
                  for (const [h, v] of curve) if (v < worst) { worst = v; worstH = h; }
                  return (
                    <span style={{ color: "var(--muted2)" }}>
                      · max margin used (concurrent, @ stop-derived lev): <b title="What the exchange would actually block: each trade's notional ÷ its leverage (lev = 1÷(1.5×stop distance), capped at the contract max) — ≈1.5× the rupee risk per trade. Notional shown alongside for reference.">₹{Math.round(maxMar).toLocaleString("en-IN")}</b>
                      <span style={{ color: "var(--faint2)" }}> (notional ₹{Math.round(maxCap).toLocaleString("en-IN")})</span>
                      {" "}· worst running P&L: <b className={worst < 0 ? "negative" : "positive"}
                        title={worstH ? `combined unrealized low on ${new Date(worstH).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" })} IST (hourly, ${covered}/${filtered.length} trades covered by kline history)` : ""}>
                        {worst < 0 ? `−₹${Math.abs(worst).toLocaleString("en-IN")}` : "never negative"}</b>
                      {covered < filtered.length && <span style={{ color: "var(--faint2)" }}> ({filtered.length - covered} uncovered)</span>}
                    </span>
                  );
                })()}
                {partial.length > 0 && <span style={{ color: "#06b6d4" }}>
                  filters: {partial.map(k => `${FACET_TITLES[k]}: ${sel[k].size === 0 ? "none" : [...sel[k]].slice(0, 6).join(", ") + (sel[k].size > 6 ? "…" : "")}`).join(" · ")}
                </span>}
              </div>
            );
          })()}
          {Object.keys(facets).map(k => (
            <ChipGroup key={k} title={FACET_TITLES[k]} values={facets[k]} selected={sel[k] || new Set(facets[k])}
              onToggle={v => toggle(k, v)} onSetAll={on => setAll(k, on)} onOnly={v => only(k, v)}
              stats={stats(FACET_FNS[k])} sortByNet={k === "symbol"} searchable={k === "symbol"} />
          ))}
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted2)", width: 60 }}>Views</span>
            <input value={viewName} onChange={e => setViewName(e.target.value)} placeholder="view name…"
              style={{ background: "var(--panel3)", color: "var(--text2)", border: "1px solid var(--border2)", borderRadius: 10, padding: "2px 8px", fontSize: 11, width: 110 }} />
            <button className="btn btn-sm btn-secondary" disabled={!viewName.trim()} onClick={() => {
              const v = { name: viewName.trim(), period, fromDate, toDate,
                sel: Object.fromEntries(Object.keys(facets).map(k => [k, sel[k].size === facets[k].length ? "ALL" : [...sel[k]]])) };
              const next = [...views.filter(x => x.name !== v.name), v];
              setViews(next); try { localStorage.setItem("cra-views", JSON.stringify(next)); } catch {}
              setViewName("");
            }}>💾 save</button>
            {views.map(v => (
              <span key={v.name} style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                <button className="btn btn-sm btn-secondary" title={`period ${v.period}`} onClick={() => {
                  setPeriod(v.period); setFromDate(v.fromDate || ""); setToDate(v.toDate || "");
                  setSel(Object.fromEntries(Object.keys(facets).map(k => {
                    const sv = v.sel[k];
                    return [k, new Set(sv === "ALL" || sv == null ? facets[k] : sv.filter(x => facets[k].includes(x)))];
                  })));
                }}>{v.name}</button>
                <button style={{ background: "none", border: "none", color: "var(--muted2)", cursor: "pointer", fontSize: 10 }}
                  onClick={() => { const next = views.filter(x => x.name !== v.name); setViews(next); try { localStorage.setItem("cra-views", JSON.stringify(next)); } catch {} }}>✕</button>
              </span>
            ))}
          </div>
        </>)}
      </div>

      <div className="cra-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(430px, 100%), 1fr))", gap: 12 }}>
        <Panel id="book" title="By book" subtitle="— the four strategies compared (the point of the validation)">
          <AggTable rows={byBook} onPick={r => openSlice(`Book: ${r.label}`, r.trades)}
            extraCol={["avg R/trade", (r) => <span className={r.r >= 0 ? "positive" : "negative"}>{(r.r / r.n).toFixed(2)}R</span>]} />
        </Panel>
        <Panel id="symbol" title="By symbol">
          <AggTable rows={bySymbol} onPick={r => openSlice(`Symbol: ${r.label}`, r.trades)} />
        </Panel>
        <Panel id="exit" title="By exit reason" subtitle="— how trades end (BE-STOP = tier banked, runner scratched)">
          <AggTable rows={byExit} onPick={r => openSlice(`Exit: ${r.label}`, r.trades)} />
        </Panel>
        <Panel id="rdist" title="R distribution" subtitle="— every trade risks the same 1R, so this is the shape of the strategy">
          <AggTable rows={rDist} onPick={r => openSlice(`R bucket: ${r.label}`, r.trades)} />
        </Panel>
        <Panel id="days" title="By day" subtitle="— net per calendar day (UTC), newest first, with running equity">
          <Paged items={byDay} render={pageDays => (
          <div className="tl-table-wrap">
            <table className="stocks-table" style={{ width: "100%" }}>
              <thead><tr><th>Day</th><th>Trades</th><th>WR</th><th>Net P&L</th><th>Equity →</th></tr></thead>
              <tbody>
                {pageDays.map(r => (
                  <tr key={r.label} onClick={() => openSlice(`Day: ${r.label}`, r.trades)} style={{ cursor: "pointer" }}>
                    <td style={{ fontWeight: 600 }}>{r.label}</td>
                    <td>{r.n}</td>
                    <td>{Math.round(100 * r.wins / r.n)}%</td>
                    <td className={r.pnl >= 0 ? "positive" : "negative"}>{rup(r.pnl)}</td>
                    <td className={r.cum >= 0 ? "positive" : "negative"}>{rup(r.cum)}</td>
                  </tr>
                ))}
                {!byDay.length && <tr><td colSpan={5} style={{ color: "var(--muted2)" }}>no closed trades in this slice</td></tr>}
              </tbody>
            </table>
          </div>
          )} />
        </Panel>
        <Panel id="invested" title="Invested vs return @ stop-derived leverage" subtitle="— margin at lev = 1÷(1.5×stop): the stop always fires before liquidation">
          <div className="tl-table-wrap">
            <table className="stocks-table" style={{ width: "100%" }}>
              <thead><tr><th>Book</th><th>Trades</th>
                <th title="Sum of each trade's margin = notional ÷ (contract max × fraction). Sequential, not concurrent — see the funding planner on the Crypto tab for peak concurrent needs.">Invested (Σ margin)</th>
                <th>Avg margin/trade</th><th>Net P&L</th>
                <th title="Net P&L ÷ Σ margin — return on the capital actually deployed at the leverage rule">Return</th></tr></thead>
              <tbody>
                {invested.rows.map(r => (
                  <tr key={r.label} onClick={() => openSlice(`Book: ${r.label}`, r.trades)} style={{ cursor: "pointer" }}>
                    <td style={{ fontWeight: 600 }}>{r.label}</td>
                    <td>{r.n}</td>
                    <td>₹{Math.round(r.inv).toLocaleString("en-IN")}</td>
                    <td>₹{r.n ? Math.round(r.inv / r.n).toLocaleString("en-IN") : 0}</td>
                    <td className={r.pnl >= 0 ? "positive" : "negative"}>{rup(r.pnl)}</td>
                    <td className={r.roi >= 0 ? "positive" : "negative"} style={{ fontWeight: 700 }}>{r.roi >= 0 ? "+" : ""}{r.roi.toFixed(1)}%</td>
                  </tr>
                ))}
                {!invested.rows.length && <tr><td colSpan={6} style={{ color: "var(--muted2)" }}>no trades in this slice</td></tr>}
              </tbody>
              {invested.rows.length > 0 && <tfoot><tr style={{ fontWeight: 700, borderTop: "2px solid var(--border2)" }}>
                <td>ALL</td><td>{invested.totN}</td>
                <td>₹{Math.round(invested.totInv).toLocaleString("en-IN")}</td>
                <td>₹{invested.totN ? Math.round(invested.totInv / invested.totN).toLocaleString("en-IN") : 0}</td>
                <td className={invested.totPnl >= 0 ? "positive" : "negative"}>{rup(invested.totPnl)}</td>
                <td className={invested.totRoi >= 0 ? "positive" : "negative"}>{invested.totRoi >= 0 ? "+" : ""}{invested.totRoi.toFixed(1)}%</td>
              </tr></tfoot>}
            </table>
          </div>
          <div style={{ fontSize: 10, color: "var(--muted2)", marginTop: 4 }}>
            Invested = margin per trade at stop-derived leverage (1 ÷ 1.5×stop-distance, capped at the contract max) — the setting where liquidation can never beat the stop-loss (≈1.5× the risk on most trades). Trades run sequentially, so this is turnover of capital, not a float requirement; Return = net P&L on that deployed margin.
          </div>
        </Panel>
        <Panel id="costs" title="Charges" subtitle="— where the friction goes">
          <div className="tl-table-wrap">
            <table className="stocks-table" style={{ width: "100%" }}>
              <thead><tr><th></th><th>Total</th><th>Per trade</th><th>vs net P&L</th></tr></thead>
              <tbody>
                {[["Trading fees (entry+tier+exit)", sum.fees], ["Funding (longs pay / shorts receive)", sum.funding], ["All charges", sum.fees + sum.funding]].map(([l, v]) => (
                  <tr key={l}>
                    <td style={{ fontWeight: 600 }}>{l}</td>
                    <td>₹{Math.round(v).toLocaleString("en-IN")}</td>
                    <td>₹{sum.n ? Math.round(v / sum.n).toLocaleString("en-IN") : 0}</td>
                    <td style={{ color: "var(--muted2)" }}>{sum.pnl ? Math.abs(100 * v / sum.pnl).toFixed(1) + "%" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      {slice && <SliceDrawer group={slice} onClose={() => setSlice(null)} onPickTrade={t => setDetail(t)} planLev={Math.max(1, Number(st.config?.planLeverage ?? 10))} planFrac={Math.min(1, Math.max(0.05, Number(st.config?.planLevFrac ?? 0.4)))} specs={st.contractSpecs} tradeOpen={!!detail} />}
      {detail && <MoneyDrawer t={detail} charges={chargesOf(detail)} live={null} offsetRight={slice ? "min(860px, 96vw)" : undefined}
        lev={st.contractSpecs?.[detail.symbol]?.maxLeverage} cfg={st.config} params={st.params} onClose={() => setDetail(null)} />}
    </div>
  );
}
